<template>
  <fragment>
    <div class="row">
      <div class="col-12 col-lg-10 offset-lg-1">
        <basic-portlet title="Audio">
          <template #portlet_actions>
            <a v-if="feed_uuid" :href="feed_url" class="btn btn-ss mr-2">
              <i class="la la-arrow-circle-o-left" /> Back to Podcast
            </a>
            <share-btn :episode="episode" />
          </template>
          <div class="row">
            <div v-if="!switching" class="col-12 mb-2">
              <player-iframe-preview :item="episode"
                                     :show-embed-code="false"
                                     :show-tools="false"
                                     :show-listen-link="false"
                                     layout-override="legacy" />
            </div>
            <div v-if="!switching" class="col-12 text-right">
              <button class="btn btn-ss" @click="switching = true">
                Change Audio
              </button>
            </div>

            <div v-if="switching" class="col-12">
              <item-storage-select v-model="new_storage_asset" />
              <button class="btn btn-ss ml-2 mt-4 float-right"
                      @click="switching = false; new_storage_asset = null">
                Cancel
              </button>

              <button v-if="new_storage_asset" class="btn btn-ss-standout mt-4 float-right" @click="() => confirmStorageAssetChange(new_storage_asset)">
                <i class="la la-save" /> Confirm
              </button>

              <confirm-modal name="confirm-storage-change"
                             confirm-icon="la la-save"
                             title="Are you sure?"
                             icon="la la-exclamation-triangle">
                <p><strong>Switching storage will result in loss of chapter markers and metadata!</strong></p>
                <p>The storage asset will be renamed to match the item name.</p>
              </confirm-modal>
            </div>
          </div>
        </basic-portlet>
        <basic-portlet title="General Settings">
          <general-settings />
        </basic-portlet>

        <basic-portlet title="Publish Settings">
          <publish-settings />
        </basic-portlet>

        <basic-portlet v-if="can_access_people" title="People">
          <people-settings />
        </basic-portlet>

        <basic-portlet title="Advanced">
          <advanced-settings />
        </basic-portlet>
        <meta-data v-if="episode && episode.storage" :storage-uuid="episode.storage.uuid" />
        <sticky-control-bar>
          <button type="button" class="btn btn-ss-danger" @click="confirmModalThen([episode], () => awaitThenRedirect(feed_url, destroyItem, episode), 'confirm-episode-delete')">
            Delete
          </button>
          <button type="button" class="float-right btn btn-ss-standout" @click="validateAndSave($validator, true)">
            Update Episode
          </button>
        </sticky-control-bar>
      </div>
      <delete-confirm-modal name="confirm-episode-delete" :entity-type="getItemLabel()">
        You are about to delete {{ getItemLabel() }} "<span class="font-weight-bold">{{ staged_models?.at(0)?.name }}"</span>!
      </delete-confirm-modal>
    </div>
  </fragment>
</template>

<script>
import { awaitThenRedirect } from "Lib/ajax-util";
import { mapWritableState, mapActions } from 'pinia';
import { useEpisodeManageStore } from "VueStore/app/episodeManage";
import BasicPortlet from "VueComponents/BasicPortlet";
import MetaData from "VuePartials/App/Ffmpeg/MetadataForm";
import GeneralSettings from "VuePartials/Items/EpisodeSettings/General";
import PublishSettings from "VuePartials/Items/EpisodeSettings/Publishing";
import AdvancedSettings from "VuePartials/Items/EpisodeSettings/Advanced";
import PeopleSettings from "VuePartials/Items/EpisodeSettings/People";
import StickyControlBar from "VueComponents/StickyControlBar";
import VeeValidate from "vee-validate";
import ShareBtn from "VuePartials/Items/EpisodeSettings/ShareBtn";
import PlayerIframePreview from "VueComponents/PlayerIframePreview";
import ItemStorageSelect from "VueComponents/ItemCreation/ItemStorageSelect";
import ConfirmModal from "VueModals/ConfirmModal";
import DeleteConfirmModal from 'VueComponents/VJSModals/DeleteConfirmModal';

Vue.use(VeeValidate, { inject: false, useConstraintAttrs: false });
VeeValidate.config.fieldsBagName = "veeFields";

export default {
  components: {
    ConfirmModal,
    DeleteConfirmModal,
    ItemStorageSelect,
    PlayerIframePreview,
    ShareBtn,
    BasicPortlet,
    MetaData,
    GeneralSettings,
    PublishSettings,
    PeopleSettings,
    AdvancedSettings,
    StickyControlBar,
  },
  /**
   * In order to validate forms inside child components we need to provide the parent instance of validator
   * Each component that needs validating will need to include the PodcastValidateMixin which references this parent
   * via injection.
   * @returns {*}
   */
  provide() {
    return { parentValidator: this.$validator };
  },
  data() {
    return {
      item: window.item,
      feed: window.feed,
      embed_code: null,
      feed_uuid: window.feed_uuid,
      switching: false,
      new_storage_asset: null,
    };
  },
  computed: {
    ...mapWritableState(useEpisodeManageStore, ['episode']),
    feed_url() {
      return `/podcast/${this.feed_uuid}/edit`;
    },
    can_access_people() {
      return this.permissionsCanAccessFeature('people') && this.permissionsCanDoAction('api.people.list');
    },
  },
  beforeMount() {
    this.setPodcast(this.feed);
    this.setEpisode(this.item);
    this.configureEmbedCode();
  },
  methods: {
    awaitThenRedirect,
    async destroyItem(item) {
      return [
        await axios.delete(`/api/v1/items/${item.uuid}`),
      ].map(res => res.data);
    },
    ...mapActions(useEpisodeManageStore, ['setEpisode']),
    ...mapActions(useEpisodeManageStore, ['validateAndSave']),
    ...mapActions(useEpisodeManageStore, ['setStorageAsset']),
    ...mapActions(useEpisodeManageStore, ['setPodcast']),
    configureEmbedCode() {
      const default_url = this.episode.player_data.default_url;
      default_url.replace('square', 'legacy');
      this.embed_code = `<iframe src="${default_url}" width="100%"  frameborder="0" ></iframe>`;
    },
    async confirmStorageAssetChange(new_asset) {
      const res = await this.fireModal(`confirm-storage-change`);
      if (res.data.confirm) {
        await this.switchStorageAsset(new_asset);
      }
    },
    async switchStorageAsset(new_asset) {
      try {
        const response = await axios.patch(`/api/v1/items/${this.episode.uuid}/switch-storage`, {
          storage_uuid: new_asset.uuid,
        });
        toastr.success("Successfully switched storage asset. Reloading...");
        this.setStorageAsset(response.data.data);
        this.switching = false;
      } catch (error) {
        this.handleAxiosError(error);
      }
    },
  },
};
</script>
